import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Sparkles,
  Copy,
  Check,
  Share2,
  MessageCircle,
  Linkedin,
  Mail,
  Twitter,
  ArrowLeft,
  Lock,
  Trophy,
  Users,
  Loader2,
  Download,
  Clock,
  CheckCircle2,
  TrendingUp,
  PartyPopper,
} from "lucide-react";
import confetti from "canvas-confetti";
import { useRewards } from "@/hooks/useRewards";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { toPng } from "html-to-image";
import WeKitLogo from "@/components/WeKitLogo";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import {
  fetchMyReferralAssets,
  fetchMyReferralStats,
  fetchRecentInvites,
  fetchSocialProof,
  shareMessageFor,
  shareUrlFor,
  type ReferralAssets,
  type RecentInvite,
  type SocialProof,
  type ShareChannel,
  REWARD_TIERS,
  nextTier,
  earnedTiers,
} from "@/lib/referral";
import QrPoster from "@/components/refer/QrPoster";
import FunnelPanel from "@/components/refer/FunnelPanel";
import SourcesPanel from "@/components/refer/SourcesPanel";

export default function Refer() {
  const { user } = useAuth();
  const { rewards, has: hasReward } = useRewards();
  const [assets, setAssets] = useState<ReferralAssets | null>(null);
  const [stats, setStats] = useState({ total: 0, converted: 0, pending: 0 });
  const [recent, setRecent] = useState<RecentInvite[]>([]);
  const [proof, setProof] = useState<SocialProof | null>(null);
  const [firstName, setFirstName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showStickyCta, setShowStickyCta] = useState(false);
  const posterRef = useRef<HTMLDivElement>(null);
  const heroCtaRef = useRef<HTMLDivElement>(null);
  const seenRewardsRef = useRef<Set<string> | null>(null);

  // Celebrate any newly-unlocked tiers since last visit
  useEffect(() => {
    if (rewards.length === 0) return;
    const seenKey = `wekit_seen_rewards_${user?.id ?? "anon"}`;
    if (seenRewardsRef.current === null) {
      try {
        const stored = JSON.parse(localStorage.getItem(seenKey) ?? "[]");
        seenRewardsRef.current = new Set(stored);
      } catch {
        seenRewardsRef.current = new Set();
      }
    }
    const seen = seenRewardsRef.current;
    const fresh = rewards.filter((r) => !seen.has(r.reward_key));
    if (fresh.length > 0) {
      const tiers = REWARD_TIERS.filter((t) =>
        fresh.some((f) => f.reward_key === t.key),
      );
      tiers.forEach((t, i) => {
        setTimeout(() => {
          toast.success(`${t.badge} badge unlocked.`, {
            description: t.unlock,
            duration: 6000,
          });
        }, i * 600);
      });
      confetti({
        particleCount: 120,
        spread: 80,
        origin: { y: 0.6 },
        colors: ["#a855f7", "#ec4899", "#fde68a", "#a5f3fc"],
      });
      fresh.forEach((f) => seen.add(f.reward_key));
      try {
        localStorage.setItem(seenKey, JSON.stringify([...seen]));
      } catch {
        /* ignore */
      }
    }
  }, [rewards, user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    let active = true;
    (async () => {
      const [a, s, r, p, prof] = await Promise.all([
        fetchMyReferralAssets(user.id),
        fetchMyReferralStats(user.id),
        fetchRecentInvites(user.id, 5),
        fetchSocialProof(),
        supabase
          .from("profiles")
          .select("full_name")
          .eq("id", user.id)
          .maybeSingle(),
      ]);
      if (!active) return;
      setAssets(a);
      setStats(s);
      setRecent(r);
      setProof(p);
      setFirstName(
        prof.data?.full_name ? prof.data.full_name.split(" ")[0] : null,
      );
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [user]);

  // Sticky CTA when hero scrolls out of view (mobile-first)
  useEffect(() => {
    if (!heroCtaRef.current) return;
    const obs = new IntersectionObserver(
      ([entry]) => setShowStickyCta(!entry.isIntersecting),
      { threshold: 0 },
    );
    obs.observe(heroCtaRef.current);
    return () => obs.disconnect();
  }, [loading]);

  const copyLink = async () => {
    if (!assets) return;
    try {
      await navigator.clipboard.writeText(assets.url);
      setCopied(true);
      toast.success("Link copied. Share it with someone who may find it useful.");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy. Long-press the link to copy manually.");
    }
  };

  const handleNativeShare = async () => {
    if (!assets) return;
    const msg = shareMessageFor("whatsapp", firstName, assets.url);
    if (navigator.share) {
      try {
        await navigator.share({
          title: "WeKIT™ Career Clarity 360",
          text: msg.body,
          url: assets.url,
        });
      } catch {
        /* user cancelled */
      }
    } else {
      copyLink();
    }
  };

  const downloadPoster = async () => {
    if (!posterRef.current || !assets) return;
    setExporting(true);
    try {
      const dataUrl = await toPng(posterRef.current, {
        pixelRatio: 2,
        cacheBust: true,
        skipFonts: false,
      });
      const a = document.createElement("a");
      a.download = `wekit-invite-${assets.handle}.png`;
      a.href = dataUrl;
      a.click();
      toast.success("Poster downloaded — perfect for schools & events");
    } catch (e) {
      console.error(e);
      toast.error("Couldn't generate poster. Try again.");
    } finally {
      setExporting(false);
    }
  };

  const converted = stats.converted;
  const next = nextTier(converted);
  const earned = earnedTiers(converted);
  const progressToNext = next
    ? Math.min(100, Math.round((converted / next.count) * 100))
    : 100;

  const channels = useMemo(
    () =>
      [
        { key: "whatsapp" as const, icon: MessageCircle, label: "WhatsApp", color: "text-[#25D366]" },
        { key: "linkedin" as const, icon: Linkedin, label: "LinkedIn", color: "text-[#0A66C2]" },
        { key: "twitter" as const, icon: Twitter, label: "X / Twitter", color: "text-foreground" },
        { key: "email" as const, icon: Mail, label: "Email", color: "text-muted-foreground" },
      ],
    [],
  );

  return (
    <div className="min-h-screen bg-background pb-24 sm:pb-12">
      <header className="sticky top-0 z-40 border-b border-border/50 bg-background/95 backdrop-blur">
        <div className="flex items-center justify-between px-4 py-3 max-w-5xl mx-auto">
          <Link to="/dashboard" className="flex items-center gap-3 group">
            <ArrowLeft className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
            <WeKitLogo size="sm" />
          </Link>
          <Badge variant="secondary" className="gap-1 text-xs">
            <Sparkles className="h-3 w-3 text-primary" /> Invite & earn
          </Badge>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8 sm:py-12">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative text-center mb-10"
        >
          <div
            aria-hidden
            className="pointer-events-none absolute -top-10 left-1/2 -translate-x-1/2 h-64 w-[120%] max-w-[700px] opacity-60 blur-3xl"
            style={{
              background:
                "radial-gradient(closest-side, hsl(var(--primary) / 0.35), transparent)",
            }}
          />
          <div className="relative">
            {firstName && (
              <p className="text-sm text-muted-foreground mb-2">
                Welcome, {firstName}
              </p>
            )}
            <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-foreground">
              Help someone discover their{" "}
              <span className="gradient-text">purpose</span>
            </h1>
            <p className="mt-3 text-muted-foreground max-w-xl mx-auto">
              Share your link. Friends get{" "}
              <span className="text-foreground font-semibold">₹100 off</span>{" "}
              their assessment. You unlock badges, perks, and Future of Work
              features as they join.
            </p>
          </div>
        </motion.div>

        {/* Live social proof */}
        {proof && (
          <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-8 max-w-2xl mx-auto">
            <ProofStat
              label="Friends joined"
              value={proof.friendsJoined}
              icon={Users}
            />
            <ProofStat
              label="Joined today"
              value={proof.joinedToday}
              icon={TrendingUp}
              accent
            />
            <ProofStat
              label="Wekiteers"
              value={proof.totalWekiteers}
              icon={Sparkles}
            />
          </div>
        )}

        {user?.id && (
          <div className="mb-6 grid gap-6 lg:grid-cols-2">
            <FunnelPanel />
            <SourcesPanel />
          </div>
        )}



        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : !assets ? (
          <Card className="border-border/50">
            <CardContent className="p-8 text-center text-muted-foreground">
              We couldn't load your referral link. Try refreshing this page.
            </CardContent>
          </Card>
        ) : (
          <div className="grid lg:grid-cols-5 gap-6">
            {/* Share card */}
            <Card className="lg:col-span-3 border-border/50 bg-card/50 backdrop-blur overflow-hidden">
              <CardContent className="p-6 sm:p-8 space-y-6">
                <div ref={heroCtaRef}>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                    Your personal link
                  </p>
                  <div className="mt-2 flex items-center gap-2 rounded-xl border border-border bg-background px-3 py-2.5">
                    <span className="font-mono text-sm sm:text-base text-foreground truncate flex-1">
                      {assets.url}
                    </span>
                    <Button
                      size="sm"
                      variant={copied ? "secondary" : "default"}
                      onClick={copyLink}
                      className="shrink-0 gap-1.5 min-h-[44px]"
                    >
                      {copied ? (
                        <>
                          <Check className="h-4 w-4" /> Copied
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4" /> Copy
                        </>
                      )}
                    </Button>
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">
                    Coupon code:{" "}
                    <span className="font-mono font-semibold text-primary">
                      {assets.code}
                    </span>{" "}
                    · auto-applies at checkout
                  </p>
                </div>

                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">
                    Share with the right tone
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {channels.map((c) => (
                      <ShareButton
                        key={c.key}
                        href={shareUrlFor(c.key, firstName, assets.url)}
                        icon={c.icon}
                        label={c.label}
                        color={c.color}
                      />
                    ))}
                  </div>
                  <p className="mt-2 text-[11px] text-muted-foreground/80">
                    Each channel uses a different prefilled message — warm on
                    WhatsApp, polished on LinkedIn, punchy on X, story-led on
                    email.
                  </p>
                </div>

                <Button
                  onClick={handleNativeShare}
                  variant="outline"
                  className="w-full gap-2 min-h-[48px]"
                >
                  <Share2 className="h-4 w-4" /> More share options
                </Button>

                {/* QR poster */}
                <div className="pt-4 border-t border-border/50">
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <div>
                      <p className="font-display font-semibold text-foreground">
                        Invite poster
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Pin it up at school, or share it on your stories.
                      </p>
                    </div>
                    <Button
                      onClick={downloadPoster}
                      disabled={exporting}
                      size="sm"
                      className="gap-1.5 shrink-0 min-h-[44px]"
                    >
                      {exporting ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Download className="h-4 w-4" />
                      )}
                      Download
                    </Button>
                  </div>
                  {/* Preview at 1/4 scale */}
                  <div className="rounded-2xl overflow-hidden border border-border bg-black flex items-center justify-center">
                    <div
                      style={{
                        transform: "scale(0.25)",
                        transformOrigin: "top left",
                        width: 1080,
                        height: 1350,
                      }}
                    >
                      <QrPoster
                        handle={assets.handle}
                        firstName={firstName}
                        url={assets.url}
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Progress, tiers, recent invites */}
            <div className="lg:col-span-2 space-y-6">
              <Card className="border-border/50 bg-card/50 backdrop-blur">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                        Friends joined
                      </p>
                      <p className="font-display text-4xl font-bold text-foreground mt-1">
                        {converted}
                      </p>
                    </div>
                    <div className="h-12 w-12 rounded-2xl bg-primary/10 flex items-center justify-center">
                      <Users className="h-6 w-6 text-primary" />
                    </div>
                  </div>
                  {stats.pending > 0 && (
                    <p className="text-xs text-muted-foreground mt-2">
                      + {stats.pending} invite{stats.pending === 1 ? "" : "s"}{" "}
                      pending
                    </p>
                  )}

                  {next ? (
                    <div className="mt-5">
                      <div className="flex items-center justify-between text-xs text-muted-foreground mb-1.5">
                        <span>
                          {next.count - converted} away from{" "}
                          <span className="text-foreground font-semibold">
                            {next.badge}
                          </span>
                        </span>
                        <span>
                          {converted}/{next.count}
                        </span>
                      </div>
                      <Progress value={progressToNext} className="h-2" />
                      <p className="text-xs text-muted-foreground mt-2">
                        Next unlock: {next.unlock}
                      </p>
                    </div>
                  ) : (
                    <p className="mt-5 text-sm text-primary font-medium">
                      You've earned every tier. Legend status.
                    </p>
                  )}
                </CardContent>
              </Card>

              <Card className="border-border/50 bg-card/50 backdrop-blur">
                <CardContent className="p-6">
                  <h2 className="font-display font-semibold text-foreground flex items-center gap-2 mb-4">
                    <Trophy className="h-4 w-4 text-primary" /> Reward tiers
                  </h2>
                  <ul className="space-y-3">
                    {REWARD_TIERS.map((t) => {
                      const unlocked = hasReward(t.key);
                      return (
                        <li
                          key={t.count}
                          className={`flex items-start gap-3 rounded-lg p-2.5 -mx-2.5 transition-colors ${
                            unlocked ? "bg-primary/5" : ""
                          }`}
                        >
                          <div
                            className={`h-9 w-9 rounded-lg flex items-center justify-center text-lg shrink-0 ${
                              unlocked
                                ? "bg-primary/15"
                                : "bg-muted text-muted-foreground"
                            }`}
                          >
                            {unlocked ? t.emoji : <Lock className="h-4 w-4" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <p
                                className={`text-sm font-semibold flex items-center gap-1.5 ${
                                  unlocked
                                    ? "text-foreground"
                                    : "text-muted-foreground"
                                }`}
                              >
                                {t.badge}
                                {unlocked && (
                                  <span className="inline-flex items-center gap-0.5 text-[10px] uppercase tracking-wider text-primary font-bold">
                                    <PartyPopper className="h-3 w-3" /> Unlocked
                                  </span>
                                )}
                              </p>
                              <span className="text-xs text-muted-foreground shrink-0">
                                {t.count}{" "}
                                {t.count === 1 ? "friend" : "friends"}
                              </span>
                            </div>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {t.unlock}
                            </p>
                            {unlocked && t.cta && (
                              <Link
                                to={t.cta.href}
                                className="inline-flex items-center gap-1 mt-2 text-xs font-semibold text-primary hover:underline"
                              >
                                {t.cta.label} →
                              </Link>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                  <p className="mt-4 text-[11px] text-muted-foreground/80 leading-relaxed">
                    Unlocks are tracked server-side and persist forever. Refer
                    friends → unlock features automatically.
                  </p>
                </CardContent>
              </Card>

              {/* Recent invites */}
              <Card className="border-border/50 bg-card/50 backdrop-blur">
                <CardContent className="p-6">
                  <h2 className="font-display font-semibold text-foreground flex items-center gap-2 mb-4">
                    <Clock className="h-4 w-4 text-primary" /> Recent invites
                  </h2>
                  {recent.length === 0 ? (
                    <div className="text-sm text-muted-foreground py-4 text-center">
                      No invites yet. Share your link to get started.
                    </div>
                  ) : (
                    <ul className="space-y-2">
                      {recent.map((r) => (
                        <li
                          key={r.id}
                          className="flex items-center justify-between gap-2 text-sm"
                        >
                          <span className="text-muted-foreground">
                            {formatWhen(r.created_at)}
                          </span>
                          <StatusPill status={r.status} />
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        <p className="text-center text-sm text-muted-foreground mt-10 max-w-lg mx-auto">
          Together, let's make sure no one navigates the future alone.
        </p>
      </main>

      {/* Off-screen poster source for crisp export */}
      {assets && (
        <div
          aria-hidden
          style={{
            position: "fixed",
            left: -99999,
            top: 0,
            pointerEvents: "none",
          }}
        >
          <QrPoster
            ref={posterRef}
            handle={assets.handle}
            firstName={firstName}
            url={assets.url}
          />
        </div>
      )}

      {/* Sticky mobile CTA */}
      {assets && showStickyCta && (
        <motion.div
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          className="fixed bottom-0 inset-x-0 z-50 sm:hidden border-t border-border bg-background/95 backdrop-blur px-4 py-3"
          style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
        >
          <Button
            onClick={handleNativeShare}
            className="w-full min-h-[48px] gap-2"
            size="lg"
          >
            <Share2 className="h-4 w-4" /> Share your invite link
          </Button>
        </motion.div>
      )}
    </div>
  );
}

function ShareButton({
  href,
  icon: Icon,
  label,
  color,
}: {
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  color: string;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="flex flex-col items-center justify-center gap-1.5 rounded-xl border border-border bg-background hover:bg-muted/50 hover:border-primary/30 transition-all py-3 px-2 min-h-[64px]"
    >
      <Icon className={`h-5 w-5 ${color}`} />
      <span className="text-xs font-medium text-foreground">{label}</span>
    </a>
  );
}

function ProofStat({
  label,
  value,
  icon: Icon,
  accent,
}: {
  label: string;
  value: number;
  icon: React.ComponentType<{ className?: string }>;
  accent?: boolean;
}) {
  return (
    <div
      className={`rounded-2xl border px-3 py-3 sm:px-4 sm:py-4 text-center ${
        accent
          ? "border-primary/30 bg-primary/5"
          : "border-border bg-card/40"
      }`}
    >
      <Icon
        className={`h-4 w-4 mx-auto mb-1 ${
          accent ? "text-primary" : "text-muted-foreground"
        }`}
      />
      <p className="font-display text-xl sm:text-2xl font-bold text-foreground tabular-nums">
        {value.toLocaleString()}
      </p>
      <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wider mt-0.5">
        {label}
      </p>
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  if (status === "converted") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary px-2.5 py-0.5 text-xs font-medium">
        <CheckCircle2 className="h-3 w-3" /> Joined
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-muted text-muted-foreground px-2.5 py-0.5 text-xs font-medium">
      <Clock className="h-3 w-3" /> Pending
    </span>
  );
}

function formatWhen(iso: string) {
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
